import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { RoadmapItem } from "@/lib/api";
import { inlineFailure } from "@/lib/api-errors";
import { formatDate, LOCALES } from "@/lib/format";
import { useLanguage } from "@/lib/i18n/language";
import { byEmail, firstNameLabel, normalEmail } from "@/lib/people";
import { roleLabel } from "@/lib/roles";

import { DetailsCard, type DetailsContext, Failure } from "./details-parts";
import { FollowButton } from "./follow-button";
import { followFailureMessage, hasToggle } from "./utils";

interface PeopleCardProps {
  item: RoadmapItem;
  overview: DetailsContext;
  pastMonth: boolean;
}

/**
 * Who follows the item, each with their role, Volgen for the viewer (while
 * the server lets them, `can.follow`, never from a past month), and who added
 * it when, everyone by first name (`firstNameLabel`).
 */
export function PeopleCard({ item, overview, pastMonth }: PeopleCardProps) {
  const { t } = useTranslation();
  const language = useLanguage();
  // A failed follow, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const known = byEmail(overview.people);
  const name = firstNameLabel(overview.people);
  const creator = known.get(normalEmail(item.created_by)) ?? { email: item.created_by };
  return (
    <DetailsCard
      title={t("roadmap.details.people")}
      section="people"
      aside={
        item.can.follow &&
        !pastMonth && (
          <FollowButton
            itemId={item.id}
            title={item.title}
            following={hasToggle(item, "followedByMe", overview.viewer.email)}
            variant="label"
            onError={(error) => setFailure(inlineFailure({ error }))}
            onDone={() => setFailure(null)}
          />
        )
      }
    >
      <div className="space-y-2 text-sm">
        <div>
          <p className="text-muted text-xs">
            {t("roadmap.column.followers")}{" "}
            <span className="tabular-nums">({item.followers.length})</span>
          </p>
          {item.followers.length === 0 ? (
            <p className="text-muted">{t("roadmap.followers.none")}</p>
          ) : (
            <ul className="space-y-0.5">
              {item.followers.map((email) => {
                const person = known.get(normalEmail(email));
                return (
                  <li key={email} className="wrap-anywhere">
                    {name(person ?? { email })}
                    {person && (
                      <span className="text-muted">
                        , {roleLabel(person.role).toLocaleLowerCase(LOCALES[language])}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <p className="text-muted text-xs wrap-anywhere">
          {t("roadmap.details.addedBy", {
            person: name(creator),
            date: formatDate(item.created_at),
          })}
        </p>
        {failure && <Failure>{followFailureMessage(failure.error)}</Failure>}
      </div>
    </DetailsCard>
  );
}
